import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "src/dm/utils/axios";
import { APIS } from "src/dm/utils/constants";
import { DealAttachment } from "src/dm/interfaces/DealAttachment";

interface AttachmentsEnvelope {
  items: DealAttachment[];
  total: number;
}

// Admin-lookup envelope for /admin/lookups/attachment-type. The row shape
// follows the standard LookupRow (id stored as string even when the
// underlying column is numeric — `isKeyNumeric: true` on the catalog def).
// We coerce to number here so the DropDownList in the upload dialog can
// bind a typed value.
export interface AttachmentTypeRow {
  id: number;
  description: string;
  usageCount?: number;
}

interface AttachmentTypesEnvelope {
  items: { id: string; description: string; usageCount?: number }[];
}

export const useAttachmentTypes = () =>
  useQuery({
    queryKey: ["lookup-attachment-types"],
    queryFn: async (): Promise<AttachmentTypeRow[]> => {
      const response = await apiClient.get<AttachmentTypesEnvelope>(
        "/dm/V1/admin/lookups/attachment-type"
      );
      return (response.data.items ?? []).map((r) => ({
        id: Number(r.id),
        description: r.description,
        usageCount: r.usageCount,
      }));
    },
    staleTime: 5 * 60 * 1000,
  });

export const useDealAttachments = (dealId: number | undefined) =>
  useQuery({
    queryKey: ["deal-attachments", dealId],
    enabled: dealId !== undefined && dealId > 0,
    queryFn: async (): Promise<DealAttachment[]> => {
      const response = await apiClient.get<AttachmentsEnvelope>(
        `${APIS.Deals}/${dealId}/attachments`
      );
      return response.data.items;
    },
  });

export const downloadAttachmentUrl = (dealId: number, attachmentId: number) =>
  `${APIS.Deals}/${dealId}/attachments/${attachmentId}/download`;

export interface UploadAttachmentInput {
  file: File;
  description: string;
  attachmentTypeId: number;
  dealItemId?: number | null;
}

export const useUploadAttachment = (dealId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UploadAttachmentInput): Promise<DealAttachment> => {
      const form = new FormData();
      form.append("file", input.file);
      form.append("description", input.description);
      form.append("attachmentTypeId", String(input.attachmentTypeId));
      if (input.dealItemId != null) form.append("dealItemId", String(input.dealItemId));
      const response = await apiClient.post<DealAttachment>(
        `${APIS.Deals}/${dealId}/attachments`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deal-attachments", dealId] });
    },
  });
};

export const useUpdateAttachmentDescription = (dealId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      attachmentId,
      description,
    }: {
      attachmentId: number;
      description: string;
    }): Promise<void> => {
      await apiClient.put(
        `${APIS.Deals}/${dealId}/attachments/${attachmentId}`,
        { description }
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deal-attachments", dealId] });
    },
  });
};

export const useDeleteAttachment = (dealId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (attachmentId: number): Promise<void> => {
      await apiClient.delete(`${APIS.Deals}/${dealId}/attachments/${attachmentId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deal-attachments", dealId] });
    },
  });
};
